import { usePaletteCommands } from "@/components/command-palette/use-palette-commands";
import { CommandButton } from "@/components/command-palette/command-button";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { ChevronDownIcon, RefreshCwIcon, Trash2Icon } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { CustomerCompanyAction } from "../_helpers/customer-company-selection";

type CustomerCompanyActionButtonsProps = {
  isFullSyncPending: boolean;
  isBulkDeletePending: boolean;
  onSelectAction: (action: CustomerCompanyAction) => void;
};

export function CustomerCompanyActionButtons({
  isFullSyncPending,
  isBulkDeletePending,
  onSelectAction,
}: CustomerCompanyActionButtonsProps) {
  const { t } = useTranslation("masterdata");
  const isPending = isFullSyncPending || isBulkDeletePending;

  usePaletteCommands(
    isPending
      ? []
      : [
          {
            id: "customer-full-sync",
            title: t("CustomerFullSync"),
            subtitle: t("CustomerFullSyncStandard"),
            icon: <RefreshCwIcon />,
            perform: () => onSelectAction({ type: "full-sync", force: false }),
          },
          {
            id: "customer-full-sync-forced",
            title: t("CustomerFullSync"),
            subtitle: t("CustomerFullSyncForced"),
            icon: <RefreshCwIcon />,
            perform: () => onSelectAction({ type: "full-sync", force: true }),
          },
        ],
  );

  return (
    <div className="flex flex-wrap gap-2 lg:gap-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={ButtonVariant.Outlined} colorVariant={ButtonColorVariant.Secondary} disabled={isPending}>
            {isFullSyncPending ? <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} /> : <RefreshCwIcon />}
            {t("CustomerFullSync")}
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => onSelectAction({ type: "full-sync", force: false })}>
            {t("CustomerFullSyncStandard")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onSelectAction({ type: "full-sync", force: true })}>
            {t("CustomerFullSyncForced")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CommandButton
        commandId="customer-bulk-delete"
        label={t("CustomerBulkDelete")}
        icon={<Trash2Icon />}
        variant={ButtonVariant.Filled}
        colorVariant={ButtonColorVariant.Error}
        disabled={isPending}
        loading={isBulkDeletePending}
        onClick={() => onSelectAction({ type: "bulk-delete" })}
      />
    </div>
  );
}
